import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Select from 'react-select';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Label,
  FormGroup,
  Form,
  Input,
  Table,
  Row,
  Col,
  Spinner,
  UncontrolledTooltip,
  TabPane,
  CardFooter,
} from 'reactstrap';
import { useArrayState } from 'components/CustomHooks';
import { FixedCenterLoader } from 'components/Loaders';
import {
  listProducts,
  getDiscovery,
  getQuickViewDiscovery,
  getAdvancedAnalyticsFactorExposuresOptions,
} from 'services/analysis';
import Tabs from 'components/Tabs';
import { optionizeValue, optionizeAll, getValue, getDate, getYearMonth, monthDiff } from 'helpers/select';
import { MODEL_PORTFOLIOS, SAVED_PORTFOLIOS } from './constants';

const defaultCurrencyOptions = [
  { value: 'Default', label: 'No' },
  { value: 'CAD', label: 'CAD (Convert USD)' },
  { value: 'USD', label: 'USD (Convert CAD)' },
];

export const defaultMeasureOptions = [
  { value: 'cagr', label: 'Return' },
  { value: 'st_dev', label: 'Standard Deviation' },
];

export const defaultRatioOptions = [
  { value: 'sharpe', label: 'Sharpe Ratio' },
  { value: 'sortino', label: 'Sortino Ratio' },
];

export const defaultVarMetricOptions = [
  { value: 'HVaR', label: 'HVaR' },
  { value: 'CVaR', label: 'CVaR' },
  { value: 'AVaR', label: 'AVaR' },
];

export const defaultConfidenceOptions = [
  { value: 0.9, label: '90%' },
  { value: 0.95, label: '95%' },
  { value: 0.99, label: '99%' },
];

export const defaultChartOptions = [
  { value: 'rolling_beta', label: 'Beta' },
  { value: 'rolling_correl', label: 'Correlation' },
];

const defaultPeriodOptions = [
  { value: '12', label: '12 months' },
  { value: '18', label: '18 months' },
  { value: '24', label: '24 months' },
  { value: '30', label: '30 months' },
  { value: '36', label: '36 months' },
];

const emptyOption = { label: 'No Benchmark', value: '' };
const emptyFactorOption = { label: 'No Factor', value: '' };
const portfolios = [MODEL_PORTFOLIOS, SAVED_PORTFOLIOS];

function AssetSelect({
  title = 'SELECT ASSET',
  enableBenchmark = false,
  enableFactorGroups = false,
  enableFactor = false,
  enableMeasure = false,
  enableRatio = false,
  enableVarMetric = false,
  enableConfidence = false,
  enableChart = false,
  enablePeriod = false,
  factorOrBenchmark = false,
  portfoliosOnly = false,
  benchmarkRequired = false,
  databaseFilter = () => true,
  onSelect,
  onAllowed = () => true,
}) {
  const [tab, setTab] = useState(portfoliosOnly ? 'portfolio' : 'asset');
  const [dataLoading, setDataLoading] = useState(false);
  const [asset, setAsset] = useState({});
  const [factorGroupOptions, setFactorGroupOptions, selectedFactorGroupOption, setSelectedFactorGroupOption] =
    useArrayState({ initialItems: [] });
  const [benchOptions, setBenchOptions, selectedBenchOption, setSelectedBenchOption] = useArrayState({
    initialItems: benchmarkRequired ? [] : [emptyOption],
  });
  const [factorOptions, setFactorOptions, selectedFactorOption, setSelectedFactorOption] = useArrayState({
    initialItems: [],
  });
  const [allAssetOptions, setAllAssetOptions] = useState(null);
  const [databaseOptions, setDatabaseOptions, selectedDatabaseOption, setSelectedDatabaseOption] = useArrayState({
    initialItems: [],
    selectFirstItem: false,
  });
  const [assetOptions, setAssetOptions, selectedAssetOption, setSelectedAssetOption] = useArrayState({
    initialItems: [],
    selectFirstItem: false,
  });
  const [currencyOptions, setCurrencyOptions, selectedCurrencyOption, setSelectedCurrencyOption] = useArrayState({
    initialItems: defaultCurrencyOptions,
  });
  const [measureOptions, setMeasureOptions, selectedMeasureOption, setSelectedMeasureOption] = useArrayState({
    initialItems: defaultMeasureOptions,
  });
  const [ratioOptions, setRatioOptions, selectedRatioOption, setSelectedRatioOption] = useArrayState({
    initialItems: defaultRatioOptions,
  });
  const [varMetricOptions, setVarMetricOptions, selectedVarMetricOption, setSelectedVarMetricOption] = useArrayState({
    initialItems: defaultVarMetricOptions,
  });
  const [confidenceOptions, setConfidenceOptions, selectedConfidenceOption, setSelectedConfidenceOption] =
    useArrayState({
      initialItems: defaultConfidenceOptions,
    });
  const [chartOptions, setChartOptions, selectedChartOption, setSelectedChartOption] = useArrayState({
    initialItems: defaultChartOptions,
  });
  const [periodOptions, setPeriodOptions, selectedPeriodOption, setSelectedPeriodOption] = useArrayState({
    initialItems: defaultPeriodOptions,
  });

  const fetchData = async () => {
    setDataLoading(true);

    const [assetRes, benchRes, factorRes] = await Promise.all([
      getQuickViewDiscovery(),
      getDiscovery(),
      getAdvancedAnalyticsFactorExposuresOptions(),
    ]);

    const [asset] = assetRes;
    if (!asset) {
      setAsset({});
    } else {
      setAsset(asset);
      if (portfoliosOnly) {
        setDatabaseOptions(optionizeAll([MODEL_PORTFOLIOS, SAVED_PORTFOLIOS]));
      } else {
        const keys = _.keys(asset).filter(databaseFilter);
        setDatabaseOptions(optionizeAll(keys));
      }

      const _allAssetOptions = {};
      _.each(asset, (val, key) => {
        if (val) _allAssetOptions[key] = optionizeAll(val);
      });

      setAllAssetOptions(_allAssetOptions);
    }

    const [bench] = benchRes;
    if (bench) {
      const benchmarks = bench.benchmark_choice.map(optionizeValue);
      const factorGroups = bench.risk_budget_choice.map(optionizeValue);
      setBenchOptions(benchmarkRequired ? [...benchmarks] : [emptyOption, ...benchmarks]);
      setSelectedBenchOption(null);
      setFactorGroupOptions(factorGroups);
      setSelectedFactorGroupOption(factorGroups[0]);
    } else {
      setBenchOptions(benchmarkRequired ? [] : [emptyOption]);
      setSelectedBenchOption(null);
      setFactorGroupOptions([]);
      setSelectedFactorGroupOption(null);
    }

    const [factor] = factorRes;
    if (factor) {
      const factorOptions = factor.factors.map(optionizeValue);
      setFactorOptions(factorOptions);
      setSelectedFactorOption(factorOptions[0]);
    } else {
      setFactorOptions([]);
      setSelectedFactorOption(null);
    }

    setDataLoading(false);
  };

  useEffect(() => {
    if (!asset || !selectedDatabaseOption || !allAssetOptions) return;

    const _assetOptions = allAssetOptions[selectedDatabaseOption.value];
    setAssetOptions(_assetOptions);
  }, [asset, selectedDatabaseOption]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setSelectedDatabaseOption(null);
    setSelectedAssetOption(null);
  }, [tab]);

  const allowed = useMemo(() => {
    if (enableFactor && enableBenchmark && factorOrBenchmark) {
      if (selectedBenchOption?.value === '' && selectedFactorOption?.value === '') return false;
    }

    return onAllowed({
      database: selectedDatabaseOption?.value,
      product: selectedAssetOption?.value,
      converter:
        selectedDatabaseOption?.value === SAVED_PORTFOLIOS
          ? selectedCurrencyOption?.value
          : defaultCurrencyOptions[0].value,
      benchmark: enableBenchmark ? selectedBenchOption?.value : '',
      factorGroup: enableFactorGroups ? selectedFactorGroupOption?.value : '',
      factor: enableFactor ? selectedFactorOption?.value : '',
      measure: enableMeasure ? selectedMeasureOption?.value : '',
      ratio: enableRatio ? selectedRatioOption?.value : '',
      varMetric: enableVarMetric ? selectedVarMetricOption?.value : '',
      confidence: enableConfidence ? selectedConfidenceOption?.value : '',
      chart: enableChart ? selectedChartOption?.value : '',
      period: enablePeriod ? selectedPeriodOption?.value : '',
    });
  }, [
    selectedDatabaseOption,
    selectedAssetOption,
    selectedCurrencyOption,
    selectedBenchOption,
    selectedFactorGroupOption,
    selectedFactorOption,
    selectedMeasureOption,
    selectedRatioOption,
    selectedVarMetricOption,
    selectedConfidenceOption,
    selectedChartOption,
    selectedPeriodOption,
    onAllowed,
  ]);

  if (dataLoading) {
    return <FixedCenterLoader />;
  }

  const tabs = [
    {
      label: 'SELECT PORTFOLIO',
      value: 'portfolio',
      content: (
        <div className="tw-flex tw-flex-col tw-gap-0">
          <Select
            className="react-select"
            placeholder="Select Dataset..."
            name="selectedDatabaseOption"
            value={selectedDatabaseOption}
            onChange={setSelectedDatabaseOption}
            options={databaseOptions.filter((opt) => portfolios.includes(opt.value))}
          />

          <Select
            className="react-select"
            placeholder="Select Asset..."
            name="selectedAssetOption"
            value={selectedAssetOption}
            onChange={setSelectedAssetOption}
            options={assetOptions}
          />
        </div>
      ),
    },
  ];

  if (!portfoliosOnly) {
    tabs.unshift({
      label: title,
      value: 'asset',
      content: (
        <div className="tw-flex tw-flex-col tw-gap-0">
          <Select
            className="react-select"
            placeholder="Select Dataset..."
            name="selectedDatabaseOption"
            value={selectedDatabaseOption}
            onChange={setSelectedDatabaseOption}
            options={databaseOptions.filter((opt) => !portfolios.includes(opt.value))}
          />

          <Select
            className="react-select"
            placeholder="Select Asset..."
            name="selectedAssetOption"
            value={selectedAssetOption}
            onChange={setSelectedAssetOption}
            options={assetOptions}
          />
        </div>
      ),
    });
  }

  return (
    <>
      <Card>
        <CardBody>
          <div className="tw-max-w-md">
            <Tabs value={tab} onChange={setTab} className="" tabs={tabs} />
          </div>
        </CardBody>
      </Card>

      {(selectedDatabaseOption?.value === SAVED_PORTFOLIOS ||
        enableBenchmark ||
        enableFactorGroups ||
        enableFactor ||
        enableMeasure ||
        enableRatio ||
        enableVarMetric ||
        enableConfidence ||
        enableChart ||
        enablePeriod) && (
        <>
          <Card>
            <CardBody className="mt-3">
              {selectedDatabaseOption?.value === SAVED_PORTFOLIOS && (
                <Form className="form-horizontal">
                  <div className="tw-font-bold">SELECT CONVERT CURRENCY</div>
                  <FormGroup>
                    <Select
                      className="react-select"
                      name="selectedCurrencyOption"
                      value={selectedCurrencyOption}
                      onChange={setSelectedCurrencyOption}
                      options={currencyOptions}
                    />
                  </FormGroup>
                </Form>
              )}

              {enableFactorGroups && (
                <Form className="form-horizontal">
                  <div className="tw-font-bold">SELECT RISK MODEL</div>
                  <FormGroup>
                    <Select
                      className="react-select"
                      name="selectedFactorGroupOption"
                      value={selectedFactorGroupOption}
                      onChange={setSelectedFactorGroupOption}
                      options={factorGroupOptions}
                    />
                  </FormGroup>
                </Form>
              )}

              {enableFactor && (
                <Form className="form-horizontal">
                  <div className="tw-font-bold">SELECT FACTOR EXPOSURE</div>
                  <FormGroup>
                    <Select
                      className="react-select"
                      name="selectedFactorOption"
                      value={selectedFactorOption}
                      onChange={setSelectedFactorOption}
                      options={factorOptions}
                    />
                  </FormGroup>
                </Form>
              )}

              {enableBenchmark && (
                <Form className="form-horizontal">
                  <div className="tw-font-bold">SELECT BENCHMARK</div>
                  <FormGroup>
                    <Select
                      className="react-select"
                      name="selectedBenchOption"
                      value={selectedBenchOption}
                      onChange={(option) => {
                        setSelectedBenchOption(option);
                        if (factorOrBenchmark) {
                          setSelectedFactorOption(emptyFactorOption);
                        }
                      }}
                      options={benchOptions}
                    />
                  </FormGroup>
                </Form>
              )}

              {enableMeasure && (
                <Form className="form-horizontal">
                  <div className="tw-font-bold">SELECT MEASURE</div>
                  <FormGroup>
                    <Select
                      className="react-select"
                      name="selectedMeasureOption"
                      value={selectedMeasureOption}
                      onChange={setSelectedMeasureOption}
                      options={measureOptions}
                    />
                  </FormGroup>
                </Form>
              )}

              {enableRatio && (
                <Form className="form-horizontal">
                  <div className="tw-font-bold">SELECT RATIO</div>
                  <FormGroup>
                    <Select
                      className="react-select"
                      name="selectedRatioOption"
                      value={selectedRatioOption}
                      onChange={setSelectedRatioOption}
                      options={ratioOptions}
                    />
                  </FormGroup>
                </Form>
              )}

              {enableVarMetric && (
                <Form className="form-horizontal">
                  <div className="tw-font-bold">SELECT VAR METRIC</div>
                  <FormGroup>
                    <Select
                      className="react-select"
                      name="selectedVarMetricOption"
                      value={selectedVarMetricOption}
                      onChange={setSelectedVarMetricOption}
                      options={varMetricOptions}
                    />
                  </FormGroup>
                </Form>
              )}

              {enableConfidence && (
                <Form className="form-horizontal">
                  <div className="tw-font-bold">SELECT CONFIDENCE INTERVAL</div>
                  <FormGroup>
                    <Select
                      className="react-select"
                      name="selectedConfidenceOption"
                      value={selectedConfidenceOption}
                      onChange={setSelectedConfidenceOption}
                      options={confidenceOptions}
                    />
                  </FormGroup>
                </Form>
              )}

              {enableChart && (
                <Form className="form-horizontal">
                  <div className="tw-font-bold">SELECT CHART</div>
                  <FormGroup>
                    <Select
                      className="react-select"
                      name="selectedChartOption"
                      value={selectedChartOption}
                      onChange={setSelectedChartOption}
                      options={chartOptions}
                    />
                  </FormGroup>
                </Form>
              )}

              {enablePeriod && (
                <Form className="form-horizontal">
                  <div className="tw-font-bold">SELECT ROLLING PERIOD</div>
                  <FormGroup>
                    <Select
                      className="react-select"
                      name="selectedPeriodOption"
                      value={selectedPeriodOption}
                      onChange={setSelectedPeriodOption}
                      options={periodOptions}
                    />
                  </FormGroup>
                </Form>
              )}
            </CardBody>
          </Card>
        </>
      )}

      <div className="text-center">
        <Button
          className="btn-round"
          color="info"
          disabled={!allowed}
          onClick={() => {
            console.log('allowed', allowed);
            if (!allowed) return;

            onSelect({
              database: selectedDatabaseOption?.value ?? '',
              product: selectedAssetOption?.value ?? '',
              converter:
                selectedDatabaseOption?.value === SAVED_PORTFOLIOS
                  ? selectedCurrencyOption?.value ?? ''
                  : defaultCurrencyOptions[0].value,
              benchmark: enableBenchmark ? selectedBenchOption?.value ?? '' : '',
              factorGroup: enableFactorGroups ? selectedFactorGroupOption?.value ?? '' : '',
              factor: enableFactor ? selectedFactorOption?.value ?? '' : '',
              measure: enableMeasure ? selectedMeasureOption?.value ?? '' : '',
              ratio: enableRatio ? selectedRatioOption?.value ?? '' : '',
              varMetric: enableVarMetric ? selectedVarMetricOption?.value ?? '' : '',
              confidence: enableConfidence ? selectedConfidenceOption?.value ?? '' : '',
              chart: enableChart ? selectedChartOption?.value ?? '' : '',
              period: enablePeriod ? selectedPeriodOption?.value ?? '' : '',
            });
          }}
        >
          Analyze
        </Button>
      </div>
    </>
  );
}

export default AssetSelect;
